import { useState } from 'react';
import { Badge, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { completeVisit, resolveReschedule, updateVisitStatus } from '../api';
import VisitTimeline from './VisitTimeline';
import VisitCancelModal from './VisitCancelModal';
import AssignAgentModal from './AssignAgentModal';
import ConfirmModal from './ConfirmModal';
import MessageModal from './MessageModal';

// Shared visit status + step-actions block, used by BOTH the rental owner's dashboard and the
// website owner's (admin) dashboard - every backing endpoint in routes/realEstate.js already
// accepts an admin caller, so the same buttons drive a visit forward for either role. Owns its
// cancel / assign-agent modals so the dashboards stay thin.

function agentLabel(assignedTo) {
  if (!assignedTo || typeof assignedTo !== 'object') return null;
  return `${assignedTo.name}${assignedTo.phone ? ` · ${assignedTo.phone}` : ''}`;
}

// Finance popups sometimes pass a charge / pending-item row, not a RentalBooking. Those rows
// keep the visit on visitId/bookingId and use _id for the *other* document (e.g. a platform
// charge) — PATCHing that as /visits/:id 404s. Prefer the visit-scoped fields, then _id.
function visitRecordId(visit) {
  const raw = visit?.visitId || visit?.bookingId || visit?._id || visit?.id;
  if (raw == null || raw === '') return '';
  return String(raw);
}

function visitStatusReason(visit) {
  const when = visit.deliveryAssignment?.scheduledAt || visit.startDate;
  const timing = when ? ` Scheduled for ${new Date(when).toLocaleString()}.` : ' A visit time has not been scheduled yet.';
  if (visit.status === 'cancelled') return 'This site visit was cancelled; no agent action is expected.';
  if (visit.deliveryNeedsReassignment) return `The assigned agent is unavailable and a replacement is required.${timing}`;
  if (!visit.deliveryAssignment?.assignedTo) return `Awaiting assignment to an agent.${timing}`;
  if (visit.returnStatus === 'COMPLETED') return 'The assigned agent completed the visit and submitted the outcome.';
  return `Assigned to the site-visit agent role.${timing}`;
}

// The badge + timeline + agent / reschedule / cancellation sub-lines.
export function VisitStatus({ visit, compact = false }) {
  return (
    <div className={compact ? 'text-end' : ''}>
      <OverlayTrigger placement="top" overlay={<Tooltip id={`visit-status-${visitRecordId(visit)}`}>{visitStatusReason(visit)}</Tooltip>}>
        <span style={{ cursor: 'help' }} tabIndex={0}>
          <Badge bg={visit.status === 'confirmed' ? 'success' : visit.status === 'cancelled' ? 'danger' : 'warning'} className="mb-1">
            {visit.status}
          </Badge>
        </span>
      </OverlayTrigger>
      <VisitTimeline visit={visit} />
      {visit.status === 'cancelled' && visit.cancellation?.reason && (
        <div className="small text-muted mt-1">
          Cancelled by {visit.cancellation.cancelledBy}
          {visit.cancellation.cancelledAt && ` on ${new Date(visit.cancellation.cancelledAt).toLocaleString()}`}
          : "{visit.cancellation.reason}"
        </div>
      )}
      {visit.extension?.requested && visit.extension.status === 'pending' && (
        <div className="small text-muted mt-1">
          Wants to reschedule to {new Date(visit.extension.requestedEndDate).toLocaleString()}
          {visit.extension.note && <> - "{visit.extension.note}"</>}
        </div>
      )}
      {agentLabel(visit.deliveryAssignment?.assignedTo) && (
        <div className="small text-muted mt-1">
          Agent: {agentLabel(visit.deliveryAssignment.assignedTo)}
          {visit.deliveryAssignment?.scheduledAt && ` · ${new Date(visit.deliveryAssignment.scheduledAt).toLocaleString()}`}
        </div>
      )}
      {visit.deliveryNeedsReassignment && (
        <div className="small text-warning mt-1">Agent declined — needs reassignment</div>
      )}
      {visit.returnStatus === 'COMPLETED' && visit.visitReport?.remarks && (
        <div className="small mt-1 p-2 bg-light border rounded">
          <span className="fw-semibold">Visit report</span>
          {visit.visitReport.outcome && <> · {visit.visitReport.outcome.replace(/_/g, ' ')}</>}
          {visit.visitReport.completedByRole && <> · by {visit.visitReport.completedByRole}</>}
          <div className="text-muted">"{visit.visitReport.remarks}"</div>
        </div>
      )}
    </div>
  );
}

// The step-actions. `areaHint` seeds the agent picker toward whoever covers the property's area.
export function VisitActions({ visit, areaHint = '', onChanged }) {
  const [cancelVisitId, setCancelVisitId] = useState(null);
  const [assignVisit, setAssignVisit] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeRemarks, setCompleteRemarks] = useState('');
  const [completing, setCompleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [rejectRescheduleOpen, setRejectRescheduleOpen] = useState(false);

  const id = visitRecordId(visit);

  const run = async (fn) => {
    if (!id) {
      setErrorMsg('Unable to update this visit.');
      return;
    }
    try {
      await fn();
      onChanged();
    } catch (err) {
      setErrorMsg(err?.response?.data?.msg || 'Unable to update this visit.');
    }
  };

  const handleVisitAction = (status) => run(() => updateVisitStatus(id, status));

  const confirmComplete = async () => {
    setCompleting(true);
    try {
      await run(() => completeVisit(id, completeRemarks.trim()));
      setCompleteOpen(false);
      setCompleteRemarks('');
    } finally {
      setCompleting(false);
    }
  };

  const handleRescheduleDecision = (status) => run(() => resolveReschedule(id, status));

  return (
    <div className="d-flex flex-column gap-1">
      {visit.status === 'pending' && (
        <div className="d-flex gap-2">
          <Button size="sm" variant="success" onClick={() => handleVisitAction('confirmed')}>
            Accept
          </Button>
          <Button size="sm" variant="outline-danger" onClick={() => setDeclineOpen(true)}>
            Decline
          </Button>
        </div>
      )}
      {visit.extension?.requested && visit.extension.status === 'pending' && (
        <div className="d-flex gap-2">
          <Button size="sm" variant="success" onClick={() => handleRescheduleDecision('approved')}>
            Approve reschedule
          </Button>
          <Button size="sm" variant="outline-danger" onClick={() => setRejectRescheduleOpen(true)}>
            Reject
          </Button>
        </div>
      )}
      {visit.status === 'confirmed' && visit.returnStatus !== 'COMPLETED' && (
        <>
          <Button size="sm" variant="outline-secondary" onClick={() => setAssignVisit({ ...visit, _id: id })}>
            {visit.deliveryAssignment?.assignedTo ? 'Reassign agent' : 'Assign agent'}
          </Button>
          <Button size="sm" variant="outline-primary" onClick={() => setCompleteOpen(true)}>
            Mark Visited
          </Button>
          <Button size="sm" variant="outline-danger" onClick={() => setCancelVisitId(id)}>
            Cancel
          </Button>
        </>
      )}

      <VisitCancelModal
        visitId={cancelVisitId}
        show={!!cancelVisitId}
        onHide={() => setCancelVisitId(null)}
        onCancelled={() => { setCancelVisitId(null); onChanged(); }}
      />
      <AssignAgentModal
        visit={assignVisit}
        areaHint={areaHint}
        show={!!assignVisit}
        onHide={() => setAssignVisit(null)}
        onAssigned={() => { setAssignVisit(null); onChanged(); }}
      />
      <ConfirmModal
        show={declineOpen}
        onHide={() => setDeclineOpen(false)}
        onConfirm={() => { handleVisitAction('cancelled'); setDeclineOpen(false); }}
        title="Decline this visit?"
        message="The visitor will be told the request is cancelled."
        confirmLabel="Decline"
        confirmVariant="danger"
      />
      <ConfirmModal
        show={rejectRescheduleOpen}
        onHide={() => setRejectRescheduleOpen(false)}
        onConfirm={() => { handleRescheduleDecision('rejected'); setRejectRescheduleOpen(false); }}
        title="Reject this reschedule?"
        message="The original visit time stays. The visitor will be told the new time was not accepted."
        confirmLabel="Reject"
        confirmVariant="danger"
      />
      <Modal show={completeOpen} onHide={() => setCompleteOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title as="h5">Mark this visit as completed?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted">This cannot be undone from here.</p>
          <Form.Group controlId="owner-visit-remarks">
            <Form.Label>Remarks (optional)</Form.Label>
            <Form.Control
              as="textarea" rows={3} maxLength={2000}
              placeholder="Anything to note about how the visit went…"
              value={completeRemarks} onChange={(e) => setCompleteRemarks(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setCompleteOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={completing} onClick={confirmComplete}>
            {completing ? 'Saving…' : 'Mark visited'}
          </Button>
        </Modal.Footer>
      </Modal>
      <MessageModal
        show={!!errorMsg}
        onHide={() => setErrorMsg('')}
        title="Could not update visit"
        message={errorMsg}
      />
    </div>
  );
}
